/**
 * 通知ユーティリティ
 * FCMトークン取得、通知許可、購読管理を共通化
 */

import { getMessaging, getToken, onMessage, isSupported, MessagePayload } from 'firebase/messaging';
import { app } from './firebase';
import { api } from './api';

// 通知の種類
export type NotificationType =
    | 'instant_request'      // 即時精算リクエスト
    | 'settlement_complete'  // 精算完了
    | 'budget_alert'         // 予算アラート
    | 'general';             // 一般通知

// 通知データ型
export interface NotificationData {
    id: string;
    type: NotificationType;
    title: string;
    body: string;
    data?: Record<string, string>;
    isRead: boolean;
    createdAt: string;
}

// 通知クリックハンドラー型
export type NotificationClickHandler = (notification: NotificationData) => void;

// 通知クリックハンドラーの登録
const clickHandlers: Map<NotificationType, NotificationClickHandler> = new Map();

/**
 * 通知タイプごとのクリックハンドラーを登録
 */
export function registerNotificationHandler(
    type: NotificationType,
    handler: NotificationClickHandler
): void {
    clickHandlers.set(type, handler);
}

/**
 * 通知がサポートされているかチェック
 * 注: 'Notification' in window チェックは防御的プログラミング。
 * 古いブラウザやNode環境では Notification が存在しない場合がある。
 * PWAターゲットのモダンブラウザでは通常発生しないが、安全のため残している。
 */
export async function isNotificationSupported(): Promise<boolean> {
    // 防御的チェック: 古いブラウザやNode環境向け
    if (!('Notification' in window)) {
        return false;
    }

    try {
        return await isSupported();
    } catch {
        return false;
    }
}

/**
 * 通知の許可状態を取得
 * 注: Notification が存在しない環境では 'denied' を返す（防御的プログラミング）
 */
export function getNotificationPermission(): NotificationPermission {
    // 防御的チェック: 古いブラウザやNode環境向け
    if (!('Notification' in window)) {
        return 'denied';
    }
    return Notification.permission;
}

/**
 * 通知許可をリクエスト
 */
export async function requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
        console.log('Notifications not supported');
        return false;
    }

    try {
        const permission = await Notification.requestPermission();
        return permission === 'granted';
    } catch (error) {
        console.error('Failed to request notification permission:', error);
        return false;
    }
}

/**
 * FCMトークンを取得してサーバーに登録
 */
export async function subscribeToNotifications(): Promise<boolean> {
    try {
        // ブラウザサポートを先に確認
        if (!('Notification' in window)) {
            console.log('[Notifications] Notification API not supported in this browser');
            return false;
        }

        // 現在の許可状態を確認
        const currentPermission = Notification.permission;
        console.log('[Notifications] Current permission state:', currentPermission);

        // 許可が拒否されている場合
        if (currentPermission === 'denied') {
            console.log('[Notifications] Permission was previously denied. User needs to enable from browser settings.');
            return false;
        }

        // 許可が未決定の場合は許可をリクエスト
        if (currentPermission !== 'granted') {
            console.log('[Notifications] Requesting permission...');
            const permission = await Notification.requestPermission();
            console.log('[Notifications] Permission result:', permission);
            if (permission !== 'granted') {
                console.log('[Notifications] Permission not granted');
                return false;
            }
        }

        // FCMサポートを確認
        const supported = await isNotificationSupported();
        if (!supported) {
            console.log('[Notifications] FCM not supported in this browser');
            return false;
        }

        // PWAのService Workerを使用（FCMと統合済み）
        // vite-plugin-pwaが自動登録するので、既存の登録を取得するだけ
        console.log('[Notifications] Getting PWA Service Worker registration...');
        let swRegistration: ServiceWorkerRegistration | undefined;
        try {
            // PWAのService Workerを待つ（最大3秒）
            const timeout = new Promise<undefined>((resolve) =>
                setTimeout(() => resolve(undefined), 3000)
            );
            const ready = navigator.serviceWorker.ready;
            swRegistration = await Promise.race([ready, timeout]);

            if (!swRegistration) {
                console.log('[Notifications] PWA Service Worker not ready, waiting...');
                swRegistration = await navigator.serviceWorker.ready;
            }
            console.log('[Notifications] Service Worker is ready:', swRegistration.scope);
        } catch (swError) {
            console.error('[Notifications] Failed to get Service Worker:', swError);
            return false;
        }

        const messaging = getMessaging(app);
        const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

        if (!vapidKey) {
            console.log('[Notifications] VAPID key not configured. Check VITE_FIREBASE_VAPID_KEY env variable.');
            return false;
        }

        console.log('[Notifications] Getting FCM token...');

        // FCMトークンを取得（Service Worker登録を渡す）
        const token = await getToken(messaging, {
            vapidKey,
            serviceWorkerRegistration: swRegistration
        });
        if (!token) {
            console.log('[Notifications] Failed to get FCM token');
            return false;
        }

        console.log('[Notifications] FCM token obtained, registering with server...');

        // サーバーに登録
        await api.subscribePush({ fcmToken: token });
        console.log('[Notifications] Successfully subscribed to notifications');

        return true;
    } catch (error) {
        console.error('[Notifications] Failed to subscribe:', error);
        // より詳細なエラー情報を出力
        if (error instanceof Error) {
            console.error('[Notifications] Error name:', error.name);
            console.error('[Notifications] Error message:', error.message);
        }
        return false;
    }
}

/**
 * 通知の購読を解除
 */
export async function unsubscribeFromNotifications(): Promise<boolean> {
    try {
        const supported = await isNotificationSupported();
        if (!supported) {
            return true;
        }

        const messaging = getMessaging(app);
        const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

        if (!vapidKey) {
            return true;
        }

        const token = await getToken(messaging, { vapidKey });
        if (token) {
            await api.unsubscribePush({ fcmToken: token });
        }

        return true;
    } catch (error) {
        console.error('Failed to unsubscribe from notifications:', error);
        return false;
    }
}

/**
 * フォアグラウンドで通知を受信したときのリスナーを設定
 */
export function setupForegroundNotificationListener(
    onNotification: (payload: MessagePayload) => void
): () => void {
    let unsubscribe: (() => void) | null = null;

    isSupported().then((supported) => {
        if (supported) {
            const messaging = getMessaging(app);
            unsubscribe = onMessage(messaging, (payload) => {
                console.log('Foreground notification received:', payload);
                onNotification(payload);

                // ブラウザ通知を表示（フォアグラウンドでも表示したい場合）
                if (payload.notification && Notification.permission === 'granted') {
                    new Notification(payload.notification.title || '通知', {
                        body: payload.notification.body,
                        icon: '/icons/icon-192x192.png',
                    });
                }
            });
        }
    });

    return () => {
        if (unsubscribe) {
            unsubscribe();
        }
    };
}

/**
 * 通知をクリックしたときの処理
 */
export function handleNotificationClick(notification: NotificationData): void {
    const handler = clickHandlers.get(notification.type);
    if (handler) {
        handler(notification);
    }
}
